import RenderHeader from '@components/DataGrid/RenderHeader';
import RenderCell from '@components/DataGrid/RenderCell';
import { GridColDefExtended } from '@mui/x-data-grid-pro';
import { forwardRef, RefObject, useCallback, useImperativeHandle, useState } from 'react';
import { EditableCellRef, emptyLastSign } from '@components/DataGrid/types';
import { EditableCell } from '../types';
import TextField from '@forms/Components/TextInput';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import { usePatchJobMutation } from '@api/Trinium/Endpoints/Job/slice';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import { isBillOfLadingNumberValidAsync, isBookingRefNumberValidAsync } from '@api/Trinium/Endpoints/Job/types';
import { queueModal } from '@components/Modals/modalSlice';

import styles from '../../RenderCell.module.css';

const headerDisplayName = 'Booking/BOL #';

const bookingBolDefinition: GridColDefExtended<DispatchLeg> = {
  field: 'Booking/Master Bill of Lading',
  minWidth: 75,
  width: 150,
  headerName: 'Booking Reference #|||Master Bill of Lading',
  headerDisplayName,
  renderHeader: () => <RenderHeader primaryText={headerDisplayName} />,
  renderCell: ({ row }) => {
    if (row.jobCategoryTransportationType === 'Export') {
      return (
        <RenderCell
          primarySlot={
            row.bookingRefNo && (
              <span
                className={styles.bold}
                title={`Booking Ref #: ${row.bookingRefNo}`}>
                {row.bookingRefNo}
              </span>
            )
          }
        />
      );
    }

    return (
      <RenderCell
        primarySlot={
          row.masterBol && (
            <span
              className={styles.bold}
              title={`Master BoL #: ${row.masterBol}`}>
              {row.masterBol}
            </span>
          )
        }
      />
    );
  },
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { bookingRefNo, masterBol, cutoffDate } = row;
        return caseInsensitiveIncludes(value, [bookingRefNo, cutoffDate]) || caseInsensitiveIncludes(value, [masterBol]);
      });
  },
  valueGetter: (_, row: DispatchLeg) => `${row.bookingRefNo ?? ''}|||${row.masterBol ?? ''}`,
  sortable: true,
  sortComparator: (v1, v2, cellParams1) => {
    if (typeof v1 === 'string' && typeof v2 === 'string') {
      /** The packed value always contains the '|||' separator, so test the actual content for emptiness */
      const v1Cleaned = v1.replaceAll('|||', '').trim();
      const v2Cleaned = v2.replaceAll('|||', '').trim();

      /** Empties go last regardless of sort direction */
      if (!v1Cleaned && !v2Cleaned) {
        return 0;
      }
      if (!v1Cleaned) {
        return emptyLastSign(cellParams1);
      }
      if (!v2Cleaned) {
        return -emptyLastSign(cellParams1);
      }

      /* Case-insensitive comparison so values are ordered alphabetically regardless of case. */
      return v1.localeCompare(v2, undefined, { sensitivity: 'base' });
    }

    return 0;
  },
  /** Booking/BOL belongs to the Job, so edit one row at a time */
  disableBatchEdit: true,
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>) => (
    <BookingBolEditCell
      row={row}
      ref={ref}
    />
  )
};

export default bookingBolDefinition;

const BookingBolEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row }, ref) => {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [patchJob] = usePatchJobMutation();
  const [bookingBolVal, setBookingBolVal] = useState(row.bookingRefNo ?? row.masterBol);
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));
  const isExport = row.jobCategoryTransportationType.toLowerCase() === 'export';

  const submit = useCallback(async () => {
    await patchJob({
      terminalNumber,
      request: {
        id: row.jobId,
        legIds: [row.legId],
        ...(isExport ? { bookingRefNo: bookingBolVal, allowDupBookingRefNo: true } : { masterBol: bookingBolVal, allowDupMasterBol: true })
      }
    })
      .unwrap()
      .catch((error: unknown) => {
        appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
      });
  }, [isExport, patchJob, terminalNumber, bookingBolVal, row.jobId, appDispatch, row.legId]);

  const saveHandler = useCallback(async () => {
    let isNotUniqueWarning;

    if (isExport && bookingBolVal !== row.bookingRefNo) {
      isNotUniqueWarning = await isBookingRefNumberValidAsync(bookingBolVal);
    } else if (!isExport && bookingBolVal !== row.masterBol) {
      isNotUniqueWarning = await isBillOfLadingNumberValidAsync(bookingBolVal);
    } else {
      // Nothing changed, short circuit
      return;
    }

    if (isNotUniqueWarning) {
      appDispatch(
        queueModal({
          Component: 'confirmPopup',
          Props: {
            prompt: isNotUniqueWarning,
            confirmedAction: async () => {
              await submit();
            }
          }
        })
      );
      return; // Abort current submission, wait for user confirmation
    }

    // There was no unique warning, proceed with submission
    await submit();
  }, [appDispatch, bookingBolVal, row, submit, isExport]);

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  return (
    <TextField
      variant='standard'
      value={bookingBolVal}
      onChange={(event) => {
        setBookingBolVal(event.target.value);
      }}
      focused
      disabled={isLoading}
      customProps={{
        labelText: isExport ? 'Booking Ref No' : 'Master BOL',
        isDirty: false,
        errorText: undefined,
        onClear: () => {
          setBookingBolVal('');
        },
        disabledReason: isLoading ? 'Updating…' : undefined
      }}
    />
  );
});
